import { parseContentUri } from "lib/actions/assets/parseContentUri";
import { RenderableAsset } from "lib/types/assets/RenderableAsset";
import { AssetTypeAdapterInterface } from "./AssetTypeAdapterInterface";
import { AzureStorageResource } from "../AzureStorageResource";
import { Resource, IonResource } from "cesium";
import { Guard } from "lib/actions/util";
import CameraPlugin from "components/elements/CesiumViewer/core/wrapper/CameraPlugin";
import GeoJsonPlugin from "components/elements/CesiumViewer/core/wrapper/GeoJsonPlugin";

export class GeoJsonAssetTypeAdapter implements AssetTypeAdapterInterface {
  constructor(
    private readonly _geoJson: GeoJsonPlugin,
    private readonly _camera: CameraPlugin,
  ) {}

  isExisting(uri: string) {
    return this._geoJson.isExisting(uri);
  }

  isReady(uri: string) {
    return this._geoJson.isReady(uri);
  }

  isShowing(uri: string) {
    return this._geoJson.isShowing(uri);
  }

  async load(version: RenderableAsset): Promise<void> {
    Guard.require(version, "version");
    if (version.asset.type !== "gisSimpleFeature") {
      throw new Error(
        `Unsupported asset type '${version.asset.type}', must be 'gisSimpleFeature'.`,
      );
    }

    const uri = version.contentUri;
    const resource = await this.createResource(uri, version.asset.projectId);
    await this._geoJson.load(uri, resource);
  }

  show(uri: string) {
    this._geoJson.show(uri);
  }

  hide(uri: string): void {
    this._geoJson.hide(uri);
  }

  async flyTo(uri: string): Promise<void> {
    Guard.require(uri, "uri");
    const geojson = this._geoJson.get(uri);
    if (geojson) await this._camera.flyTo(geojson);
  }

  private async createResource(
    contentUri: string,
    projectId: number,
  ): Promise<Resource> {
    const uri = parseContentUri(contentUri);
    if (uri.type !== "tileset") {
      throw new Error(
        `Invalid content URI type. Type '${uri.type}' must be a 'tileset'.`,
      );
    }

    let resource: Resource = null;
    if (uri.location === "ion") {
      const ionId = +uri.value;
      if (!ionId || Number.isNaN(ionId)) {
        throw new Error(
          `Invalid Cesium Ion id. Value '${uri.value}' must be a positive integer.`,
        );
      }
      resource = await IonResource.fromAssetId(ionId);
    } else if (uri.location === "self") {
      if (!uri.value.endsWith(".geojson")) {
        throw new Error(
          `Invalid content URI value. URL '${uri.value}' must be a .geojson file.`,
        );
      }
      resource = await AzureStorageResource.fromUrl(uri.value, projectId);
    } else {
      throw new Error(
        `Invalid content URI location. Location '${uri.location}' not supported.`,
      );
    }
    return resource;
  }
}
